/**
 * I Ching — the calculated layer.
 *
 * The knowledge base in `src/data/iching.json` carries what a book carries:
 * names, structure, judgement, image and the six line texts. Everything a
 * reader normally has to work out by hand — which trigrams, which nuclear
 * hexagram, which line is central, correct or answered, what the reading turns
 * into when your line moves — is derived here instead.
 *
 * The project rule this follows is the one the BaZi tab was rebuilt on:
 * CALCULATED and INTERPRETED stay apart. Every function in this file is
 * arithmetic on the six lines. Nothing here decides what a hexagram *means* —
 * that text all comes from the data file.
 *
 * Line order is bottom → top everywhere (index 0 = 初爻, index 5 = 上爻), and
 * 1 = yang (陽), 0 = yin (陰). That is the order the classics count in, and
 * keeping one convention end to end is what makes the nuclear/reversed/palace
 * derivations one-liners instead of a source of off-by-one bugs.
 */

export interface Hexagram {
  n: number; sym: string; cn: string; pinyin: string; en: string; th: string;
  structure: string; meaning: string; lines: string[];
  hd: { gate: number; name: string; center: string; channel: string; keynote: string; partner: string };
}

export type Bits = number[]; // length 6, bottom → top, 1 = yang

// ---------------------------------------------------------------- trigrams

export interface Trigram {
  glyph: string;
  bits: [number, number, number]; // bottom → top
  cn: string; pinyin: string; en: string; th: string;
  element: string; elementTh: string;
  family: string; familyTh: string;
  image: string; imageTh: string;
  attr: string; attrTh: string;
  dir: string;
  body: string; bodyTh: string;
  animal: string; animalTh: string;
}

/**
 * The eight trigrams. `bits` is the only field the engine reads; the rest is
 * the standard 說卦傳 attribute set, which is what makes a trigram pair
 * readable at a glance rather than two abstract symbols.
 */
export const TRIGRAMS: Trigram[] = [
  { glyph: '☰', bits: [1, 1, 1], cn: '乾', pinyin: 'Qián', en: 'Heaven', th: 'ฟ้า', element: 'Metal', elementTh: 'ทอง', family: 'Father', familyTh: 'บิดา', image: 'Heaven', imageTh: 'ฟ้า', attr: 'Strong', attrTh: 'เข้มแข็ง (健)', dir: 'NW', body: 'Head', bodyTh: 'ศีรษะ', animal: 'Horse', animalTh: 'ม้า' },
  { glyph: '☱', bits: [1, 1, 0], cn: '兌', pinyin: 'Duì', en: 'Lake', th: 'ชลาลัย', element: 'Metal', elementTh: 'ทอง', family: 'Youngest daughter', familyTh: 'บุตรสาวคนเล็ก', image: 'Marsh', imageTh: 'บึง', attr: 'Joyous', attrTh: 'รื่นรมย์ (悅)', dir: 'W', body: 'Mouth', bodyTh: 'ปาก', animal: 'Sheep', animalTh: 'แพะ' },
  { glyph: '☲', bits: [1, 0, 1], cn: '離', pinyin: 'Lí', en: 'Fire', th: 'อัคนี', element: 'Fire', elementTh: 'ไฟ', family: 'Middle daughter', familyTh: 'บุตรสาวคนกลาง', image: 'Fire', imageTh: 'ไฟ', attr: 'Clinging', attrTh: 'ยึดเกาะ (麗)', dir: 'S', body: 'Eye', bodyTh: 'ดวงตา', animal: 'Pheasant', animalTh: 'ไก่ฟ้า' },
  { glyph: '☳', bits: [1, 0, 0], cn: '震', pinyin: 'Zhèn', en: 'Thunder', th: 'อสุนี', element: 'Wood', elementTh: 'ไม้', family: 'Eldest son', familyTh: 'บุตรชายคนโต', image: 'Thunder', imageTh: 'ฟ้าร้อง', attr: 'Arousing', attrTh: 'ปลุกเร้า (動)', dir: 'E', body: 'Foot', bodyTh: 'เท้า', animal: 'Dragon', animalTh: 'มังกร' },
  { glyph: '☴', bits: [0, 1, 1], cn: '巽', pinyin: 'Xùn', en: 'Wind', th: 'วายุ', element: 'Wood', elementTh: 'ไม้', family: 'Eldest daughter', familyTh: 'บุตรสาวคนโต', image: 'Wind / Wood', imageTh: 'ลม / ไม้', attr: 'Gentle', attrTh: 'ซึมซาบ (入)', dir: 'SE', body: 'Thigh', bodyTh: 'ต้นขา', animal: 'Fowl', animalTh: 'ไก่' },
  { glyph: '☵', bits: [0, 1, 0], cn: '坎', pinyin: 'Kǎn', en: 'Water', th: 'น้ำ', element: 'Water', elementTh: 'น้ำ', family: 'Middle son', familyTh: 'บุตรชายคนกลาง', image: 'Water', imageTh: 'น้ำ', attr: 'Abysmal', attrTh: 'ห้วงลึก (陷)', dir: 'N', body: 'Ear', bodyTh: 'หู', animal: 'Pig', animalTh: 'สุกร' },
  { glyph: '☶', bits: [0, 0, 1], cn: '艮', pinyin: 'Gèn', en: 'Mountain', th: 'ภูผา', element: 'Earth', elementTh: 'ดิน', family: 'Youngest son', familyTh: 'บุตรชายคนเล็ก', image: 'Mountain', imageTh: 'ภูเขา', attr: 'Still', attrTh: 'หยุดนิ่ง (止)', dir: 'NE', body: 'Hand', bodyTh: 'มือ', animal: 'Dog', animalTh: 'สุนัข' },
  { glyph: '☷', bits: [0, 0, 0], cn: '坤', pinyin: 'Kūn', en: 'Earth', th: 'ดิน', element: 'Earth', elementTh: 'ดิน', family: 'Mother', familyTh: 'มารดา', image: 'Earth', imageTh: 'แผ่นดิน', attr: 'Receptive', attrTh: 'ยอมตาม (順)', dir: 'SW', body: 'Belly', bodyTh: 'ท้อง', animal: 'Cow', animalTh: 'โค' },
];

const TRI_BY_GLYPH = new Map(TRIGRAMS.map(t => [t.glyph, t]));
const TRI_BY_KEY = new Map(TRIGRAMS.map(t => [t.bits.join(''), t]));

export const trigramOf = (bits: number[]): Trigram => TRI_BY_KEY.get(bits.join(''))!;

// ---------------------------------------------------------------- the index

export interface HexIndex {
  all: Hexagram[];
  byNum: Map<number, Hexagram>;
  /** six lines bottom → top, 1 = yang */
  bits: (n: number) => Bits;
  /** the King Wen number for a line pattern */
  numOf: (bits: Bits) => number;
  upper: (n: number) => Trigram;
  lower: (n: number) => Trigram;
}

/**
 * Build the line patterns from the data itself rather than from a hand-typed
 * table of 64 binaries.
 *
 * Every `structure` string opens with the two trigram glyphs in the order
 * "<upper> บน <lower>" — ䷂ 屯 reads "☵ น้ำ บน ☳ อสุนี", which is Water above
 * Thunder, and that is exactly what 屯 is. Reading the pair off the data means
 * the engine can never drift from the text the reader sees, and it is
 * self-checking: `assertIndex` fails loudly if the 64 pairs are not distinct.
 */
export function buildIndex(data: Hexagram[]): HexIndex {
  const bitsByNum = new Map<number, Bits>();
  const numByKey = new Map<string, number>();
  const triByNum = new Map<number, [Trigram, Trigram]>(); // [upper, lower]

  for (const h of data) {
    const glyphs = Array.from(h.structure).filter(c => TRI_BY_GLYPH.has(c));
    const up = TRI_BY_GLYPH.get(glyphs[0])!;
    const lo = TRI_BY_GLYPH.get(glyphs[1])!;
    // bottom → top: the lower trigram fills lines 1-3, the upper 4-6.
    const bits = [...lo.bits, ...up.bits];
    bitsByNum.set(h.n, bits);
    numByKey.set(bits.join(''), h.n);
    triByNum.set(h.n, [up, lo]);
  }

  return {
    all: data,
    byNum: new Map(data.map(h => [h.n, h])),
    bits: n => bitsByNum.get(n)!,
    numOf: b => numByKey.get(b.join(''))!,
    upper: n => triByNum.get(n)![0],
    lower: n => triByNum.get(n)![1],
  };
}

/**
 * Cheap integrity check, run once when the panel loads.
 *
 * The lesson that earned this function: the first parser that built
 * iching.json assumed single-syllable pinyin and silently dropped the fifteen
 * hexagrams with two-character names. Silence is the failure mode to design
 * against — so assert the counts rather than trusting them.
 */
export function assertIndex(ix: HexIndex): string[] {
  const problems: string[] = [];
  if (ix.all.length !== 64) problems.push(`expected 64 hexagrams, got ${ix.all.length}`);
  const keys = new Set(ix.all.map(h => ix.bits(h.n).join('')));
  if (keys.size !== 64) problems.push(`expected 64 distinct line patterns, got ${keys.size}`);
  for (const h of ix.all) {
    if (h.lines.length !== 6) problems.push(`hexagram ${h.n} has ${h.lines.length} lines`);
  }
  return problems;
}

// ------------------------------------------------------- derived hexagrams

/** 互卦 — the hexagram hidden inside lines 2-5: the situation's inner engine. */
export const nuclearBits = (b: Bits): Bits => [b[1], b[2], b[3], b[2], b[3], b[4]];

/** 錯卦 — every line flipped: the same situation seen from the opposite side. */
export const oppositeBits = (b: Bits): Bits => b.map(x => (x ? 0 : 1));

/** 綜卦 — the whole figure turned upside down: the other party's view of it. */
export const reversedBits = (b: Bits): Bits => [...b].reverse();

/** 之卦 — what the reading becomes once the moving lines have moved. */
export const changedBits = (b: Bits, moving: number[]): Bits =>
  b.map((x, i) => (moving.includes(i + 1) ? (x ? 0 : 1) : x));

// ------------------------------------------------------------ line offices

export interface LineRole {
  pos: number;          // 1..6
  yang: boolean;
  central: boolean;     // 中 — lines 2 and 5 sit at the heart of their trigram
  correct: boolean;     // 當位/正 — odd places want yang, even places want yin
  ruler: boolean;       // 君位 — line 5, the seat of the ruler
  answered: boolean;    // 應 — 1↔4, 2↔5, 3↔6 answer only across opposite polarity
  partner: number;      // the position it answers to
  riding: boolean;      // 乘剛 — a yin line sitting on top of a yang one
  carrying: boolean;    // 承陽 — a yin line supporting a yang one above it
  sole: boolean;        // the only line of its polarity — classically pivotal
}

export function lineRole(b: Bits, pos: number): LineRole {
  const i = pos - 1;
  const yang = !!b[i];
  const partner = pos <= 3 ? pos + 3 : pos - 3;
  const yangCount = b.reduce((s, x) => s + x, 0);
  return {
    pos,
    yang,
    central: pos === 2 || pos === 5,
    correct: pos % 2 === 1 ? yang : !yang,
    ruler: pos === 5,
    answered: b[partner - 1] !== b[i],
    partner,
    riding: !yang && pos > 1 && !!b[i - 1],
    carrying: !yang && pos < 6 && !!b[i + 1],
    sole: (yang && yangCount === 1) || (!yang && yangCount === 5),
  };
}

export const allRoles = (b: Bits): LineRole[] => [1, 2, 3, 4, 5, 6].map(p => lineRole(b, p));

/** Name for a line the way the classics call it: 初九, 六二, 上六 … */
export function lineName(b: Bits, pos: number): string {
  const yang = !!b[pos - 1];
  const num = yang ? '九' : '六';
  const place = pos === 1 ? '初' : pos === 6 ? '上' : ['二', '三', '四', '五'][pos - 2];
  // 初 and 上 put the place first, the middle four put the polarity first.
  return pos === 1 || pos === 6 ? place + num : num + place;
}

// ------------------------------------------------------- 京房八宮 palaces

export const PALACE_RANKS = ['本宮', '一世', '二世', '三世', '四世', '五世', '遊魂', '歸魂'] as const;
export type PalaceRank = typeof PALACE_RANKS[number];

export interface Palace {
  palace: Trigram;   // the trigram the palace is named after
  rank: PalaceRank;
  /** 世爻 — the line that stands for the querent in this palace position */
  world: number;
  /** 應爻 — the line that stands for the other party, always 世 ± 3 */
  other: number;
}

const WORLD_BY_RANK: Record<PalaceRank, number> = {
  本宮: 6, 一世: 1, 二世: 2, 三世: 3, 四世: 4, 五世: 5, 遊魂: 4, 歸魂: 3,
};

/**
 * Jing Fang's eight palaces, generated rather than tabulated.
 *
 * Each palace starts from a doubled trigram and walks up flipping one more
 * line from the bottom; 遊魂 ("wandering soul") flips the fourth line back,
 * and 歸魂 ("returning soul") restores the whole lower trigram. Generating it
 * means the 64 assignments can be *checked* — `buildPalaces` throws if the
 * walk does not land on 64 distinct hexagrams, which is the only honest way to
 * ship a table this easy to get subtly wrong.
 */
export function buildPalaces(ix: HexIndex): Map<number, Palace> {
  const out = new Map<number, Palace>();
  for (const t of TRIGRAMS) {
    let bits: Bits = [...t.bits, ...t.bits];
    const flip = (b: Bits, pos: number): Bits => b.map((x, i) => (i === pos - 1 ? (x ? 0 : 1) : x));
    const walk: Bits[] = [bits];
    for (const pos of [1, 2, 3, 4, 5]) { bits = flip(bits, pos); walk.push(bits); }
    bits = flip(bits, 4); walk.push(bits);                    // 遊魂
    bits = [...t.bits, ...bits.slice(3)]; walk.push(bits);    // 歸魂 — lower trigram home
    walk.forEach((b, i) => {
      const rank = PALACE_RANKS[i];
      out.set(ix.numOf(b), { palace: t, rank, world: WORLD_BY_RANK[rank], other: ((WORLD_BY_RANK[rank] + 2) % 6) + 1 });
    });
  }
  if (out.size !== 64) throw new Error(`palace walk produced ${out.size} hexagrams, expected 64`);
  return out;
}

// ------------------------------------------------------------- text fields

export interface Meaning { desc: string; judgment: string; image: string; }

/**
 * The knowledge base keeps the core meaning, the 卦辭 (Judgment) and the 大象
 * (Image) in one paragraph, marked by the words "Judgment:" and "Image:".
 * They are three different kinds of statement — a gloss, an oracle and a piece
 * of advice — so the panel shows them as three, split here at read time rather
 * than by regenerating the data file.
 */
export function splitMeaning(meaning: string): Meaning {
  const iJ = meaning.indexOf('Judgment:');
  const iI = meaning.indexOf('Image:');
  if (iJ < 0 && iI < 0) return { desc: meaning.trim(), judgment: '', image: '' };
  const end = iI >= 0 ? iI : meaning.length;
  return {
    desc: meaning.slice(0, iJ >= 0 ? iJ : end).trim(),
    judgment: iJ >= 0 ? meaning.slice(iJ + 'Judgment:'.length, end).trim() : '',
    image: iI >= 0 ? meaning.slice(iI + 'Image:'.length).trim() : '',
  };
}
